'use client'
import { useEffect, useState } from 'react'
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { useTranslations } from 'next-intl'
import { usePathname } from '@/i18n/navigation'
import { LanguageSelector } from './LanguageSelector';
import Image from 'next/image';

/**
 * NAV: navigation bar items
 * key is unique and is the translation key of the menu (found in translate files)
 * href the url of the menu item
 * hash is the hash of the menu item, if it is a section in the same page or null if not
 *
 */
const NAV: any[] = [
  {
    key: 'products',
    href: '',
    hash: 'products-kuik',
  },
  {
    key: 'for_banks',
    href: '',
    hash: 'for-banks',
  },
  // {
  //   key: 'empsa',
  //   href: '',
  //   hash: 'empsa',
  // }
]

/**
 * Header component
 * key is unique and is the translation key of the menu (found in translate files)
 * href the url of the menu item
 * hash is the hash of the menu item, if it is a section in the same page or null if not
 *
 */
export const Header = () => {
  const t = useTranslations('Header');
  const pathname = usePathname();
  const [activeTab, setActiveTab] = useState(pathname === '/' ? 'about' : '');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [isScrolled, setIsScrolled] = useState(false);

  // when a nav item with a hash is clicked while already on the home page,
  // Next.js doesn't scroll since the route itself doesn't change - do it manually
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, link: any) => {
    setActiveTab(link.key);
    setIsMobileMenuOpen(false);

    if (link.hash && pathname === '/') {
      e.preventDefault();
      const target = document.getElementById(link.hash);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
        window.history.pushState(null, '', `#${link.hash}`);
      }
    }
  };

  // handle scroll to add background and shadow to header when scrolled down
  useEffect(() => {
    const handleScroll = () => {
      // Toggle background/shadow once scrolled past 20px
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`w-full bg-transparent px-6 sm:px-10 lg:px-16 py-4 sticky top-0 z-50 sticky top-0 z-50 w-full
        transition-all duration-300 ease-in-out
        ${isScrolled
          ? 'bg-white/80 backdrop-blur-md py-3 shadow-sm border-b border-gray-200/50'
          : 'bg-transparent py-5 border-b border-gray-200'
        }`}>
      <div className="max-w-8xl mx-auto flex items-center justify-between">
        {/* Logo Section */}
        <Link href="/" className="flex items-center gap-2.5 group shrink-0">
          <Image src="/images/logo.webp" width="40" height ="40" alt="mpay logo"/>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-9">
          {NAV?.map((link: any) => {
            const isActive = activeTab === link.key;
            const href = link.hash ? `/#${link.hash}` : link.href;
            return (
              <Link
                key={link.key}
                href={href}
                onClick={(e) => handleNavClick(e, link)}
                className={`text-md font-semibold transition-colors duration-200 ${isActive
                    ? 'color-primary underline underline-offset-8 decoration-2'
                    : 'text-black hover:color-primary'
                  }`}
              >
                {t(link.key)}
              </Link>
            );
          })}
        </nav>

       <div className="hidden lg:flex items-center space-x-6">
          <LanguageSelector />
        {/* Desktop Actions: Removed no where to link */}
          {/*  <Link
            href="/#contact"
            className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold pl-5 pr-4 py-2.5 rounded-lg transition-colors duration-200"
          >
            {t('partner_with_us')}
            <ArrowRight size={16} />
          </Link> */}
        </div>

        {/* Mobile Hamburger Button */}
        <div className="flex items-center space-x-3 lg:hidden">
          <LanguageSelector />
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 text-black focus:outline-none"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              /* Close Icon */
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              /* Hamburger Icon */
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

      </div>

      {/* Mobile Menu Slide-down Drawer */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 w-full bg-white border-b border-gray-100 shadow-lg px-6 py-6 transition-all duration-300">
          <nav className="flex flex-col space-y-4">
            {NAV?.map((link: any) => {
              const isActive = activeTab === link.key;
              const href = link.hash ? `/#${link.hash}` : link.href;
              return (
                <Link
                  key={link.key}
                  href={href}
                  onClick={(e) => handleNavClick(e, link)}
                  className={`text-base font-semibold py-1 transition-colors ${isActive ? 'text-red-600' : 'text-black'
                    }`}
                >
                  {t(link.key)}
                </Link>
              );
            })}
            {/* <Link
              href="/#contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="inline-flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-5 py-3 rounded-lg transition-colors duration-200 mt-2"
            >
              {t('partner_with_us')}
              <ArrowRight size={16} />
            </Link> */}
          </nav>
        </div>
      )}
    </header>
  );
}